import { QueryClient } from '@tanstack/react-query';
import { isPublicKey } from '@/api/resource';

const ONE_DAY = 24 * 60 * 60 * 1000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Registered resources provide their own policy. Infinity is the safe
      // fallback: a query outside the registry must not independently refetch
      // and tear a revision-coherent screen.
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: ONE_DAY,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

/**
 * API responses are authorization-scoped even when their query keys are not
 * user-specific. Purge the in-memory cache whenever the authenticated identity
 * ends or changes so a later, more restricted user cannot reuse those rows.
 */
export async function clearPrivateQueryCache(): Promise<void> {
  // Private queries only, as the name says. `queryClient.clear()` also destroys
  // in-flight PUBLIC ones, and a query removed mid-flight is aborted and left
  // pending with nothing to re-drive it. On the token-gated public screens the
  // sibling `GET /me` 401s by definition, so `clear()` raced every page load and
  // won often enough to hang it on a permanent loading state — with no log line
  // anywhere, because an abort is deliberately not reported as an error.
  queryClient.removeQueries({ predicate: (query) => !isPublicKey(query.queryKey) });
}
